/** /s/<sessionId> — the student's answer screen. States: waiting, open, sent/changed, closed,
 * result, and — when the teacher ends the lesson — the end screen, which is where a guest is
 * told what this lesson showed about them and how to get to the next one. The student sees
 * only their own answer and, after a reveal, the class aggregate. */
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { capabilities, type FocusStatus } from "../platform/capabilities";
import { useSurface } from "../platform/surface";

import { serviceUrl } from "../api/base";
import { deviceId, nextSeq } from "../live/device";
import type { ServerMessage } from "../live/socket";
import { asSignups, refreshRegistration, registrationFor, signupsOf, type RegistrationStatus } from "../live/studentIdentity";
import { recallJoin } from "../live/studentSession";
import { BottomNav } from "../student/BottomNav";
import { LeaveLesson } from "../student/LeaveLesson";
import { LessonResult } from "../student/LessonResult";
import { recordRound } from "../student/lessonRecord";
import { SignupFootnote } from "../student/SignupFootnote";
import { ConnectCard } from "../telegram/ConnectCard";
import { useLive } from "../live/useLive";
import { answersLatest, mine, optionState as computeOptionState, sameAnswer, shownAnswer, verdictOf } from "../live/verdict";
import type { Answer, Pending } from "../live/verdict";
import { Button, DistributionBars, OptionCard, StatusBar, Timer, useRemaining } from "../ui";
import type { OptionState } from "../ui";
import { nsKey } from "../platform/storage";

export function Student() {
  const { sessionId } = useParams({ strict: false }) as { sessionId: string };
  const navigate = useNavigate();
  const joined = useMemo(() => recallJoin(sessionId), [sessionId]);
  useSurface("student");
  useEffect(() => {
    if (!joined) void navigate({ to: "/j" });
  }, [joined, navigate]);
  if (!joined) return null;
  return <StudentLive sessionId={sessionId} token={joined.token} guestToken={joined.guest_token ?? null} />;
}

function StudentLive({ sessionId, token, guestToken }: { sessionId: string; token: string; guestToken: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canClaim = !!guestToken;
  // STORY-122: a chat is linked from the student's own screen, as the guest principal the
  // join minted for this device. The affordance is gated on the lesson, not on who is signed
  // in: a standard course mints a guest token for every joiner, a guest-only course mints
  // none, so the card is absent there by construction -- for a signed-in account too.
  const canLink = !!guestToken;
  const telegramOffer = canLink ? <ConnectCard token={guestToken} id="student-telegram" title={t("tg.title")} lede={t("tg.body")} /> : null;
  const dev = useMemo(() => deviceId("student"), []);
  // When the socket is down, an answer goes over plain HTTP with the same idempotency key.
  const fallback = async (payload: Record<string, unknown>): Promise<ServerMessage | null> => {
    if (!("response" in payload)) return null;
    try {
      const res = await fetch(`${serviceUrl(`/session/v1/sessions/${sessionId}/responses`)}?token=${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload.response),
      });
      return res.ok ? ((await res.json()) as ServerMessage) : null;
    } catch {
      return null;
    }
  };
  const { snapshot, conn, last, send, skewMs } = useLive(sessionId, token, dev, fallback);
  const focus = capabilities.focusMode;
  // STORY-080: without a DND plugin (iOS, browsers) the reduced set is full screen, keep-awake
  // and our own alerts silenced; Do Not Disturb is never claimed there
  const reduced = !focus && (capabilities.platform === "ios" || !!capabilities.wakeLock);
  const [focusChoice, setFocusChoice] = useState<"ask" | "on" | "off">(() => (localStorage.getItem(nsKey("focus_consent")) as "on" | "off" | null) ?? "ask");
  const [focusStatus, setFocusStatus] = useState<FocusStatus | null>(null);
  useEffect(() => {
    if (!focus) return;
    void focus.status().then(setFocusStatus);
  }, [focus]);
  const liveState = snapshot?.state ?? "lobby";
  useEffect(() => {
    if (!reduced || focusChoice !== "on" || liveState === "ended") {
      localStorage.removeItem(nsKey("lesson_focus_active"));
      return;
    }
    localStorage.setItem(nsKey("lesson_focus_active"), "1"); // our own push and toasts stay quiet
    void capabilities.wakeLock?.acquire();
    // requestFullscreen is a no-op in WKWebView: hide the system bars on the shells too
    void capabilities.systemBars?.hide();
    const el = document.documentElement as HTMLElement & { requestFullscreen?: () => Promise<void> };
    void el.requestFullscreen?.().catch(() => undefined);
    return () => {
      localStorage.removeItem(nsKey("lesson_focus_active"));
      void capabilities.wakeLock?.release();
      void capabilities.systemBars?.show();
      void (document as Document & { exitFullscreen?: () => Promise<void> }).exitFullscreen?.().catch(() => undefined);
    };
  }, [reduced, focusChoice, liveState]);
  useEffect(() => {
    if (!focus || focusChoice !== "on") return;
    if (liveState === "ended") {
      void focus.exit().then(setFocusStatus);
      return;
    }
    void focus.enter().then(setFocusStatus);
    return () => {
      void focus.exit();
    };
  }, [focus, focusChoice, liveState]);
  // STORY-113: the sign-up policy travels with the snapshot, and the teacher's answer to a
  // waiting request only reaches this device if it asks. One call on arrival, and again when
  // the lesson ends, which is the other moment the footnote is shown.
  const [signupStatus, setSignupStatus] = useState<RegistrationStatus>(() => registrationFor(sessionId)?.status ?? "none");
  const ended = snapshot?.state === "ended";
  useEffect(() => {
    void refreshRegistration(sessionId, token).then(setSignupStatus);
  }, [sessionId, token, ended]);
  // live snapshot first, the remembered policy while the socket is still coming up
  const signups = asSignups(snapshot?.policy?.signups) ?? signupsOf(sessionId);
  const item = snapshot?.item ?? null;
  const itemKey = item ? `${item.item_id}:${item.attempt}` : "";
  const options = item?.snapshot.options ?? [];
  // what the server has counted for this student on THIS item and attempt; a record from
  // another attempt is discarded, so a re-vote round starts blank
  const my = mine(snapshot?.my_response, item?.item_id, item?.attempt);
  /** The teacher released this lesson's results. The field is on the frame's contract; the hub
   * currently sends it to staff only, so this is false on a phone until that one line moves
   * out of its `if staff:` block — LessonResult has the second, server-side route in. */
  const released = snapshot?.released === true;

  // Keep the lesson's own record as the server states it, round by round, so the end screen
  // can show this student what the lesson showed about them. Nothing is judged here: `correct`
  // is whatever the server said, and null until it says anything (src/live/verdict.ts).
  const myCorrect = my?.correct ?? null;
  const answered = !!my;
  useEffect(() => {
    if (!item) return;
    recordRound(sessionId, {
      item_id: item.item_id,
      attempt: item.attempt,
      stem: item.snapshot.spoken ? "" : item.snapshot.stem,
      answered,
      correct: myCorrect,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, itemKey, answered, myCorrect]);

  const [answer, setAnswer] = useState<Answer>(null);
  const [confidence, setConfidence] = useState<0 | 1 | 2 | null>(null);
  const [sent, setSent] = useState<Answer>(null);
  const [text, setText] = useState("");
  /** Why the screen just changed under the student: a tap the server would not take. */
  const [notice, setNotice] = useState<null | "late" | "lateNone" | "kept">(null);
  // ordering (STORY-041): the student sees the steps shuffled once per item and reorders them
  const shuffled = useMemo(() => {
    const keys = options.map((o) => o.key);
    let seed = 0;
    for (const ch of `${item?.item_id ?? ""}:${item?.attempt ?? 0}:${dev}`) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
    for (let i = keys.length - 1; i > 0; i--) {
      seed = (seed * 1103515245 + 12345) >>> 0;
      const j = seed % (i + 1);
      [keys[i], keys[j]] = [keys[j]!, keys[i]!];
    }
    return keys;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.item_id, item?.attempt]);
  const [order, setOrder] = useState<string[] | null>(null);
  const currentOrder = order ?? shuffled;

  // the newest submission this device made for the item on screen; a reply to an older one is
  // history (two taps on a slow link, a queued frame flushed on reconnect) and must not
  // repaint the screen or accuse a change that the server did take
  const pendingRef = useRef<Pending>(null);

  // A new item (or re-vote) resets the local answer.
  useEffect(() => {
    setAnswer(null);
    setSent(null);
    setConfidence(null);
    setText("");
    setNotice(null);
    setOrder(null);
    pendingRef.current = null;
  }, [itemKey]);

  // the handlers below read the answer as it is now, not as it was when they were created
  const answerRef = useRef<Answer>(null);
  useEffect(() => {
    answerRef.current = answer;
  }, [answer]);

  // One handler for both replies to a submission, and only for the reply to the newest one:
  // the server names the answer it holds for this student, a refused late tap is rolled back
  // to that record and explained, and a duplicate whose stored answer differs is corrected
  // instead of passing as success.
  useEffect(() => {
    const d = last?.data as Record<string, unknown> | undefined;
    if (!last || !d || !item) return;
    if (last.kind !== "response_ack" && !(last.kind === "error" && d.code === "response_rejected")) return;
    if (!answersLatest(d, item.item_id, item.attempt, pendingRef.current)) return;
    const counted = (d.counted_answer ?? null) as Answer;
    setSent(counted);
    if (!sameAnswer(counted, answerRef.current)) {
      setAnswer(counted);
      setNotice(d.accepted === false ? (counted === null ? "lateNone" : "late") : "kept");
    } else if (d.accepted !== false) setNotice(null);
  }, [last]); // eslint-disable-line react-hooks/exhaustive-deps

  // Presentation only: at the visible 0:00 the options go inert, so the engine's grace covers
  // taps already in flight and never one the student was invited to make too late.
  const remaining = useRemaining(item?.deadline ?? null, skewMs);
  const accepting = !item?.deadline || (remaining ?? 1) > 0;
  const open = !!item && !item.closed && snapshot?.state === "item_open" && accepting;
  const type = item?.snapshot.type ?? "single";
  const revealed = item?.revealed === "correct";
  const agg = snapshot?.aggregate ?? null;
  // once the item stops accepting answers the screen shows the server's record, not the tap
  const shown = shownAnswer(open, answer, my);
  const verdict = verdictOf(revealed, my);

  // STORY-081: tell the session when this device leaves or returns to the app. The teacher
  // sees a count; names only when the course policy says so, which is shown below.
  //
  // ONE COUNTER, not one per kind. The store is idempotent on (session_id, device_id, seq),
  // so two counters on one device in one session mean an attention event and an answer
  // eventually claim the same seq — and the answer comes second, is stored as a duplicate,
  // is never applied to the engine, and the student is told their tap "did not count". Found
  // 2026-09-11 by leaving the app and answering: the first answer of the lesson was lost.
  useEffect(() => {
    const report = () => send({ attention: { seq: nextSeq(`student:${sessionId}`), away: document.visibilityState !== "visible" } });
    document.addEventListener("visibilitychange", report);
    window.addEventListener("pagehide", report);
    return () => {
      document.removeEventListener("visibilitychange", report);
      window.removeEventListener("pagehide", report);
    };
  }, [send, sessionId]);

  function submit(next: Answer = answer) {
    if (!item || next === null || next === "") return;
    setAnswer(next);
    const seq = nextSeq(`student:${sessionId}`);
    pendingRef.current = { key: itemKey, seq };
    send({ response: { seq, item_id: item.item_id, attempt: item.attempt, answer: next, confidence } });
  }

  function optionState(key: string): OptionState {
    return computeOptionState(key, { open, revealed, shown, sent, my });
  }

  return (
    <main className="font-system mx-auto flex min-h-dvh max-w-md flex-col gap-4 p-4">
      {!ended && snapshot?.policy?.attention_flags && (
        <p className="rounded-[var(--radius-control)] bg-[color:var(--surface-2)] px-3 py-2 text-xs text-[color:var(--ink-2)]" role="note">{t("student.attentionPolicy")}</p>
      )}
      {!ended && (focus || reduced) && focusChoice === "ask" && (
        <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm shadow-sm" aria-live="polite">
          <h2 className="mb-1 font-semibold">{t("focus.title")}</h2>
          <ul className="mb-2 list-disc pl-5 text-[color:var(--ink-2)]">
            <li>{t("focus.fullscreen")}</li>
            <li>{t("focus.awake")}</li>
            {focus ? <li>{t("focus.dnd")}</li> : <li>{t("focus.ownAlerts")}</li>}
          </ul>
          {reduced && capabilities.platform === "ios" && (
            <details className="mb-2 text-xs text-[color:var(--ink-2)]">
              <summary className="cursor-pointer">{t("focus.ios.title")}</summary>
              <p className="mt-1">{t("focus.ios.body")}</p>
              <ol className="mt-1 list-decimal pl-5">
                <li>{t("focus.ios.step1")}</li>
                <li>{t("focus.ios.step2")}</li>
                <li>{t("focus.ios.step3")}</li>
              </ol>
              <p className="mt-1">{t("focus.ios.noDnd")}</p>
            </details>
          )}
          <p className="mb-3 text-xs text-[color:var(--ink-2)]">{t("focus.private")}</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="min-h-[var(--control-h)] rounded-[var(--radius-control)] bg-[color:var(--accent)] px-4 text-[color:var(--accent-ink)]" onClick={async () => { localStorage.setItem(nsKey("focus_consent"), "on"); setFocusChoice("on"); if (focus && focusStatus && !focusStatus.permitted) setFocusStatus(await focus.requestPermission()); }}>{t("focus.on")}</button>
            <button type="button" className="min-h-[var(--control-h)] rounded-[var(--radius-control)] border px-4" onClick={() => { localStorage.setItem(nsKey("focus_consent"), "off"); setFocusChoice("off"); }}>{t("focus.off")}</button>
          </div>
        </section>
      )}
      {!ended && focus && focusChoice === "on" && focusStatus && !focusStatus.permitted && (
        <p className="text-xs text-[color:var(--ink-2)]">{t("focus.noPermission")} <button type="button" className="underline" onClick={async () => setFocusStatus(await focus.requestPermission())}>{t("focus.grant")}</button></p>
      )}
      {/* The one door out of a lesson, and the answer to "I had to close the app to join the
          next one". It is the leading item of the header, away from the options, and leaving
          costs nothing: the join screen offers the sitting back. */}
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
          <LeaveLesson />
          {!ended && <StatusBar state={conn === "live" ? "live" : conn === "reconnecting" ? "reconnecting" : "offline"} />}
        </div>
        {!ended && item && <Timer deadline={item.closed ? null : item.deadline} skewMs={skewMs} />}
      </header>

      {/* The lesson is over: the screen follows it instead of sitting on a question nobody can
          answer any more. This is also the only place a guest — who has no bottom navigation
          and no account — is shown their own result. */}
      {ended && (
        <section className="flex flex-1 flex-col gap-4">
          <h1 className="text-xl font-semibold">{t("student.end.title")}</h1>
          <p className="text-[color:var(--ink-2)]">{t("student.end.next")}</p>
          <LessonResult sessionId={sessionId} guestToken={guestToken} released={released} />
          {/* One offer, not two: where the course allows sign-ups the footnote below is the
              way to keep this history, and the older claim link would say the same thing a
              second time in different words. */}
          {canClaim && signups === "off" && (
            <p className="text-sm">
              <Link to="/claim" search={{ session: sessionId }} className="inline-flex min-h-[var(--control-h)] items-center underline">
                {t("student.claim")}
              </Link>
            </p>
          )}
          {/* STORY-113: the footnote sits where the lesson is not running — the moment after
              joining and the moment after it ends — and only where the teacher allows it. */}
          <SignupFootnote policy={signups} sessionId={sessionId} status={signupStatus} />
          {telegramOffer}
          <Button block className="mt-auto" onClick={() => void navigate({ to: "/j" })}>
            {t("student.end.joinAnother")}
          </Button>
        </section>
      )}

      {!ended && !item && (
        <section className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-[color:var(--ink-2)]">
          <span aria-hidden className="text-4xl">◌</span>
          <p>{t("student.waiting")}</p>
          <SignupFootnote policy={signups} sessionId={sessionId} status={signupStatus} />
          {/* between lessons' questions is the moment to read a disclosure; during one it
              would sit between the student and the tap */}
          <div className="w-full text-left">{telegramOffer}</div>
        </section>
      )}
      {!ended && canClaim && item && (
        <p className="text-center text-xs text-[color:var(--ink-2)]">
          <Link to="/claim" search={{ session: sessionId }} className="underline">{t("student.claimShort")}</Link>
        </p>
      )}

      {!ended && item && (
        <section className="flex flex-1 flex-col gap-4">
          {item.snapshot.spoken ? (
            <h1 className="flex items-center gap-3 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-lg font-semibold shadow-sm">
              <span aria-hidden className="text-2xl">🗣︎</span>
              {t("student.listen")}
            </h1>
          ) : (
            <h1 className="text-xl font-semibold break-words">{item.snapshot.stem}</h1>
          )}

          {(type === "single" || type === "multi" || type === "hotspot") && (
            <div role={type === "multi" ? "group" : "radiogroup"} aria-label={t("student.options")} className="grid gap-3">
              {options.map((o) => (
                <OptionCard
                  key={o.key}
                  label={o.key}
                  multi={type === "multi"}
                  state={optionState(o.key)}
                  image={o.image ? { src: o.image, alt: o.label || t("student.imageOption", { key: o.key }) } : undefined}
                  bare={!o.label}
                  onSelect={() => {
                    if (!open) return;
                    if (type === "multi") {
                      const cur = Array.isArray(answer) ? answer : [];
                      setAnswer(cur.includes(o.key) ? cur.filter((k) => k !== o.key) : [...cur, o.key]);
                    } else {
                      submit(o.key); // single choice: one tap sends; the student can still change until close
                    }
                  }}
                >
                  {o.label}
                </OptionCard>
              ))}
            </div>
          )}

          {type === "ordering" && (
            <div className="flex flex-col gap-2" role="list" aria-label={t("student.orderList")}>
              {currentOrder.map((key, i) => {
                const o = options.find((x) => x.key === key);
                return (
                  <div key={key} role="listitem" className="flex items-center gap-2 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-3 shadow-sm">
                    <span className="bignum w-6 text-center">{i + 1}</span>
                    <span className="min-w-0 flex-1 break-words">{o?.label ?? key}</span>
                    <button type="button" className="min-h-[var(--control-h)] min-w-[var(--control-h)] rounded-[var(--radius-control)] border" aria-label={t("student.moveUp")} disabled={!open || i === 0} onClick={() => setOrder(currentOrder.map((k, j) => (j === i - 1 ? currentOrder[i]! : j === i ? currentOrder[i - 1]! : k)))}>↑</button>
                    <button type="button" className="min-h-[var(--control-h)] min-w-[var(--control-h)] rounded-[var(--radius-control)] border" aria-label={t("student.moveDown")} disabled={!open || i === currentOrder.length - 1} onClick={() => setOrder(currentOrder.map((k, j) => (j === i + 1 ? currentOrder[i]! : j === i ? currentOrder[i + 1]! : k)))}>↓</button>
                  </div>
                );
              })}
              <Button block disabled={!open} onClick={() => submit([...currentOrder])}>{sameAnswer(sent, answer) && answer !== null ? t("student.sentOrder") : t("student.sendOrder")}</Button>
            </div>
          )}

          {(type === "numeric" || type === "short_text") && (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                submit(type === "numeric" ? Number(text) : text);
              }}
            >
              <input
                className="field min-h-[var(--control-h)] flex-1 text-lg"
                name="answer"
                aria-label={t("student.answerField")}
                enterKeyHint="send"
                autoComplete="off"
                inputMode={type === "numeric" ? "decimal" : "text"}
                maxLength={200}
                value={text}
                disabled={!open}
                onChange={(e) => setText(e.target.value)}
              />
              <Button type="submit" disabled={!open || !text}>{t("student.send")}</Button>
            </form>
          )}

          {item.snapshot.confidence_enabled !== false && open && (
            <fieldset>
              <legend className="mb-2 text-sm text-[color:var(--ink-2)]">{t("student.confidence")}</legend>
              <div className="flex items-center justify-between gap-2">
              {([0, 1, 2] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-pressed={confidence === c}
                  className="min-h-[var(--control-h)] flex-1 rounded-[var(--radius-control)] border text-sm"
                  style={{ borderColor: confidence === c ? "var(--accent)" : "var(--line)", background: confidence === c ? "color-mix(in srgb, var(--accent) 12%, var(--surface))" : "var(--surface)" }}
                  onClick={() => {
                    setConfidence(c);
                    if (answer !== null && sent !== null) submit(answer);
                  }}
                >
                  {confidence === c && <span aria-hidden>✓ </span>}{t(`student.conf.${c}`)}
                </button>
              ))}
              </div>
            </fieldset>
          )}

          {type === "multi" && open && (
            <Button block onClick={() => submit(answer)} disabled={!Array.isArray(answer) || answer.length === 0}>
              {sent === null ? t("student.send") : t("student.update")}
            </Button>
          )}

          {/* one sentence per line: a notice about a tap that did not count and the verdict
              can both be true at once, and they must not run together */}
          <p aria-live="polite" className="text-sm text-[color:var(--ink-2)]">
            {sent !== null && open && <span className="block">{t("student.sentHint")}</span>}
            {sent !== null && !open && !item.closed && <span className="block">{t("student.timeUp")}</span>}
            {notice === "late" && <span className="block">{t("student.lateNoChange")}</span>}
            {notice === "lateNone" && <span className="block">{t("student.lateNoAnswer")}</span>}
            {notice === "kept" && <span className="block">{t("student.notCounted")}</span>}
            {item.closed && !revealed && <span className="block">{my || sent !== null ? t("student.closed") : t("student.closedNoAnswer")}</span>}
            {revealed && <span className="block">{verdict === "correct" ? t("student.correct") : verdict === "incorrect" ? t("student.incorrect") : t("student.revealedNeutral")}</span>}
          </p>

          {agg && item.revealed && (
            <DistributionBars
              bars={agg.options.map((o) => ({ key: o.key, label: o.key, count: o.count, correct: o.correct }))}
              total={agg.answered}
              mode={revealed ? "revealed" : "closed"}
              counts={false}
            />
          )}
        </section>
      )}
      <BottomNav />
    </main>
  );
}
